import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from 'store';

import { DeviceOutcome, isFailureOutcome } from '../core';

const selectDevicesState = (state: RootState) => state.devices;

export const selectMicrophoneOutcome = createSelector(selectDevicesState, (state) => state.microphone);
export const selectCameraOutcome = createSelector(selectDevicesState, (state) => state.camera);

/** True while a `getUserMedia` acquisition is in flight — drives the reactive overlay. */
export const selectIsAcquiringDevices = createSelector(selectDevicesState, (state) => state.isAcquiring);

/** Raw per-kind presence (from `enumerateDevices` by `kind`); `null` until first checked. */
export const selectHasMicrophone = createSelector(selectDevicesState, (state) => state.hasMicrophone);
export const selectHasCamera = createSelector(selectDevicesState, (state) => state.hasCamera);

/**
 * Whether BOTH required input devices (microphone AND camera) are present — drives the
 * "connect a device" notice. `null` until first enumerated; `false` = a required device is
 * missing; `true` = both present. Callers should only act on the explicit `false`/`true`.
 */
export const selectHasRequiredDevices = createSelector([selectHasMicrophone, selectHasCamera], (mic, cam) =>
  mic === null || cam === null ? null : mic && cam
);

export const selectIsMicrophoneInFailureState = createSelector(selectMicrophoneOutcome, (outcome) =>
  outcome !== null ? isFailureOutcome(outcome) : false
);
export const selectIsCameraInFailureState = createSelector(selectCameraOutcome, (outcome) =>
  outcome !== null ? isFailureOutcome(outcome) : false
);

export const selectIsMicrophoneAcquired = createSelector(
  selectMicrophoneOutcome,
  (outcome) => outcome === DeviceOutcome.Granted
);
export const selectIsCameraAcquired = createSelector(
  selectCameraOutcome,
  (outcome) => outcome === DeviceOutcome.Granted
);
